// Fenêtre de partage : une carte « façon carte à collectionner » avec un kami tiré au hasard, son caractère et, en bas, « Play ZERO KAMI »
// avec son QR code. On peut flasher le QR code à l'écran, en tirer une autre carte ou la partager. Sur téléphone, « Partager »
// ouvre la feuille de partage du téléphone (l'image de la carte, la baseline et le lien), celle d'Android dans l'appli Google Play
// (shareFromAndroidApp : sa WebView n'a pas navigator.share) ; ailleurs, ou si elle n'est pas disponible
// ou échoue, il copie dans le presse-papiers une version HTML (la carte en image, dont le texte alternatif est la baseline du jeu,
// et le lien) et une version texte (la baseline et le lien).
// Dessin : ui/cardPainter.js (canvas, définition fixe, affiché à n'importe quelle taille) ; données : core/shareCard.js.
// Génération locale (aucun appel réseau, QR code compris). L'URL ne change jamais pendant une session (pas de routage).
// En développement, une page ouverte sur localhost n'est pas joignable depuis un téléphone : le QR code pointe alors vers
// l'adresse réseau local du serveur (voir vite.config.js, /__lan-url).
import { $, escapeHTML } from './dom.js';
import { createDialog } from './dialog.js';
import { qrMatrix } from './qrcode.js';
import { paintShareCard, themeColors, CARD_WIDTH, CARD_HEIGHT } from './cardPainter.js';
import { legalParams } from './legalText.js';
import { cardForKami, cardFileName, shareableKamis, createCardDeck } from '../core/shareCard.js';
import { DOUBLE_TAP_MS } from '../config/constants.js';
import { GAME_URL } from '../config/gameUrl.js';
import { callNative, isNativeAndroid } from '../config/native.js';

/** L'URL est-elle une adresse de cette machine seulement (localhost, 127.x, ::1) ? */
export function isLoopbackUrl(url) {
  try {
    const { hostname } = new URL(url);
    return hostname === 'localhost' || hostname === '::1' || hostname === '[::1]' || /^127\./.test(hostname);
  } catch {
    return false;
  }
}

/** Demande au serveur de développement son adresse réseau local ; null s'il n'en donne pas. */
async function fetchLanUrl() {
  try {
    const response = await fetch('/__lan-url', { cache: 'no-store' });
    if (!response.ok) return null;
    const { url } = await response.json();
    return typeof url === 'string' ? url : null;
  } catch {
    return null;
  }
}

/** Adresse partagée (QR code, lien copié ou envoyé) : celle de la configuration (config/gameUrl.js, GAME_URL), sans paramètres (?lang=fr) ni ancre : celui qui scanne choisit sa langue. */
export function shareUrl() {
  return GAME_URL;
}

/**
 * Charge le portrait d'un kami ; null si l'image ne charge pas ou tarde trop (la carte se dessine quand même, sans portrait).
 * `load`/`error` plutôt que `decode()` : ce dernier peut ne jamais se terminer (page en arrière-plan) et bloquerait la carte.
 */
export function loadPortrait(src, { ImageCtor = Image, timeoutMs = 4000 } = {}) {
  return new Promise((resolve) => {
    const image = new ImageCtor();
    const timer = setTimeout(() => resolve(null), timeoutMs);
    const done = (value) => { clearTimeout(timer); resolve(value); };
    image.onload = () => done(image);
    image.onerror = () => done(null);
    image.src = src;
  });
}

/** Largeur (px) de la carte jointe au presse-papiers : assez grande pour garder le QR code lisible, assez petite pour rester légère. */
export const SHARE_IMAGE_WIDTH = 480;

/** La carte en image PNG (adresse `data:`) à la largeur voulue. Synchrone : le geste de l'utilisateur doit rester valable pour la copie. */
export function cardImageUrl(canvas, width = SHARE_IMAGE_WIDTH, doc = document) {
  const small = doc.createElement('canvas');
  small.width = width;
  small.height = Math.round((canvas.height * width) / canvas.width);
  small.getContext('2d').drawImage(canvas, 0, 0, small.width, small.height);
  return small.toDataURL('image/png');
}

/**
 * Ce qui part dans le presse-papiers :
 *  - html : la carte en image (texte alternatif = la baseline du jeu), cliquable vers le lien, puis le lien
 *  - text : la baseline et le lien, rien d'autre
 */
export function shareContent({ baseline, url, imageUrl }) {
  const href = escapeHTML(url);
  return {
    html: `<p><a href="${href}"><img src="${imageUrl}" alt="${escapeHTML(baseline)}" width="300"></a></p><p><a href="${href}">${href}</a></p>`,
    text: `${baseline}\n${url}`,
  };
}

/**
 * Copie dans le presse-papiers, le plus riche possible : HTML + texte ; à défaut le texte seul (navigateur sans ClipboardItem), puis
 * `execCommand('copy')` (page sans accès à l'API, ex. HTTP sur le réseau local). Renvoie 'rich', 'text' ou 'failed'.
 */
export async function copyToClipboard({ html, text }, { nav = navigator, doc = document, Item = globalThis.ClipboardItem } = {}) {
  if (nav.clipboard?.write && Item) {
    try {
      await nav.clipboard.write([new Item({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      })]);
      return 'rich';
    } catch {
      // refusé ou non pris en charge : on retombe sur le texte seul
    }
  }
  if (nav.clipboard?.writeText) {
    try {
      await nav.clipboard.writeText(text);
      return 'text';
    } catch {
      // idem
    }
  }
  try {
    const area = doc.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.cssText = 'position:fixed;top:0;left:0;opacity:0';
    doc.body.appendChild(area);
    area.select();
    const copied = !!doc.execCommand?.('copy');
    area.remove();
    return copied ? 'text' : 'failed';
  } catch {
    return 'failed';
  }
}

/**
 * La feuille de partage du téléphone, seulement sur un écran tactile qui sait partager (navigator.share). Sur ordinateur, même quand
 * le navigateur sait partager, on garde la copie dans le presse-papiers.
 */
export function nativeShareAvailable({ nav = navigator, win = window } = {}) {
  return typeof nav.share === 'function' && !!win.matchMedia?.('(pointer: coarse)').matches;
}

/**
 * Ouvre la feuille de partage du téléphone. Avec l'image : l'image, et le texte « baseline + lien » (certaines applications ignorent
 * le champ `url` quand un fichier est joint : le lien est donc dans le texte). Sans image (pas encore prête, ou non partageable) :
 * titre, baseline et lien. Renvoie 'shared', 'cancelled' (feuille fermée sans choisir), 'failed' ou 'unsupported'.
 * Doit être appelée directement dans le geste de l'utilisateur : aucun await avant.
 */
export async function shareNatively({ title, text, url, file }, { nav = navigator } = {}) {
  if (typeof nav.share !== 'function') return 'unsupported';
  const withFile = !!file && !!nav.canShare?.({ files: [file] });
  const data = withFile ? { title, text: `${text}\n${url}`, files: [file] } : { title, text, url };
  try {
    await nav.share(data);
    return 'shared';
  } catch (error) {
    return error?.name === 'AbortError' ? 'cancelled' : 'failed';
  }
}

/** Le contenu d'un fichier en base64, sans l'en-tête `data:` : c'est ainsi que l'image passe à l'appli native. */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).slice(String(reader.result).indexOf(',') + 1));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Dans l'appli Android (Google Play), la feuille de partage d'Android, ouverte par le plugin ZeroKamiNative de l'appli
 * (android/…/ZeroKamiNativePlugin.java) : l'image de la carte, et le texte « baseline + lien ». Mêmes réponses que shareNatively :
 * 'shared' une fois une appli choisie, 'cancelled' (feuille fermée sans choisir), 'failed' (pas encore d'image, ou le natif a
 * échoué : la copie prend le relais).
 */
export async function shareFromAndroidApp({ title, text, url, file }, { call = callNative, toBase64 = blobToBase64 } = {}) {
  if (!file) return 'failed';
  try {
    const data = await toBase64(file);
    await call('ZeroKamiNative', 'share', { data, fileName: file.name, mimeType: file.type || 'image/png', title, text: `${text}\n${url}` });
    return 'shared';
  } catch (error) {
    return error?.code === 'cancelled' ? 'cancelled' : 'failed';
  }
}

/** La carte en fichier PNG, à la pleine définition ; null si le navigateur ne sait pas la produire. */
export function cardToFile(canvas, fileName) {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob ? new File([blob], fileName, { type: 'image/png' }) : null), 'image/png');
    } catch {
      resolve(null);
    }
  });
}

/** Textes de la carte dans la langue courante. */
export function cardText(card, t) {
  return {
    number: t('share.card.number', { n: card.number }),
    character: t('share.card.character'),
    playLead: t('share.card.playLead'),
    scan: t('share.card.scan'),
    footer: `${t('legal.product', legalParams())} \u00b7 ${t('brand.line')}`,
  };
}

/**
 * @param {string} [url]  adresse encodée dans le QR code
 * @param {object} deps
 * @param {(key: string, params?: object) => string} deps.t
 * @param {() => string} deps.getLanguage
 * @param {() => Promise<string|null>} [deps.lookupLanUrl]  adresse réseau local, utilisée si `url` est une adresse de loopback ;
 *   par défaut le serveur de développement (rien en production)
 * @param {(card: object) => { fastGained: boolean, longLifeGained: boolean }} [deps.onShared]  la carte vient d'être partagée : renvoie les récompenses gagnées
 * @param {() => ReadonlyArray<{ familyIndex: number }>} [deps.getGallery]  la galerie du joueur : seuls ses kamis (et Ujin) ont une carte
 * @param {() => number} [deps.random]  tirage du kami (injectable pour les tests)
 * @param {(src: string) => Promise<CanvasImageSource|null>} [deps.loadImage]
 * @param {(canvas: HTMLCanvasElement, card: object, options: object) => void} [deps.draw]
 * @param {(content: {html: string, text: string}) => Promise<'rich'|'text'|'failed'>} [deps.copy]  copie dans le presse-papiers
 * @param {(canvas: HTMLCanvasElement) => string} [deps.imageUrl]  la carte en image `data:` pour la version HTML
 * @param {number} [deps.feedbackMs]  durée du message « copié », en surimpression sur la carte
 * @param {() => boolean} [deps.useNativeShare]  utiliser la feuille de partage du téléphone (écran tactile qui sait partager, ou
 *   l'appli Android)
 * @param {(data: {title: string, text: string, url: string, file: File|null}) => Promise<string>} [deps.nativeShare]
 * @param {(canvas: HTMLCanvasElement, fileName: string) => Promise<File|null>} [deps.makeFile]  la carte en fichier PNG
 */
export function createShareModal(url = shareUrl(), {
  t = (key) => key,
  getLanguage = () => 'en',
  getGallery = () => [],
  onShared = () => ({ fastGained: false, longLifeGained: false }),
  lookupLanUrl = import.meta.env?.DEV ? fetchLanUrl : null,
  random = Math.random,
  loadImage = loadPortrait,
  draw = (canvas, card, options) => paintShareCard(canvas.getContext('2d'), card, options),
  copy = copyToClipboard,
  imageUrl = cardImageUrl,
  feedbackMs = 2500,
  useNativeShare = () => isNativeAndroid() || nativeShareAvailable(),
  nativeShare = (data) => (isNativeAndroid() ? shareFromAndroidApp(data) : shareNatively(data)),
  makeFile = cardToFile,
} = {}) {
  const canvas = $('shareCardCanvas');
  const btnShare = $('btnCardShare');
  const status = $('shareStatus');
  const btnNew = $('btnCardNew');
  const btnOpen = $('btnShare');
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;

  const deck = createCardDeck(random);
  let current = null; // carte affichée
  let ticket = 0; // une carte plus récente annule le dessin de la précédente
  let addressPromise = null;
  let target = url; // l'adresse du QR code (réseau local en développement), connue une fois la carte dessinée
  let feedbackTimer = null;
  let cardFile = null; // la carte en fichier PNG, préparée dès qu'elle est dessinée : navigator.share doit partir dans le geste du doigt

  // L'adresse ne change pas : une seule résolution par session
  function address() {
    if (!addressPromise) {
      addressPromise = lookupLanUrl && isLoopbackUrl(url)
        ? lookupLanUrl().then((lan) => lan ?? url)
        : Promise.resolve(url);
    }
    return addressPromise;
  }

  async function showCard(index) {
    const mine = ++ticket;
    const card = cardForKami(index, getLanguage());
    current = card;
    canvas.setAttribute('aria-label', t('share.card.aria', { name: card.name }));
    btnShare.disabled = true;
    cardFile = null;
    const [portrait, resolved] = await Promise.all([
      loadImage(card.sprite),
      address(),
      document.fonts?.load?.('700 40px Fredoka').catch(() => {}),
    ]);
    if (mine !== ticket) return;
    target = resolved;
    draw(canvas, card, { portrait, qr: qrMatrix(target), colors: themeColors(), text: cardText(card, t) });
    btnShare.disabled = false;
    makeFile(canvas, cardFileName(card)).then((file) => { if (mine === ticket) cardFile = file; });
  }

  // Message temporaire au-dessus de la carte (le bouton garde son libellé « Partager ») ; la zone est annoncée aux lecteurs d'écran
  function announce(keys, duration = feedbackMs) {
    clearTimeout(feedbackTimer);
    status.textContent = [].concat(keys).map((key) => t(key)).join(' ');
    status.classList.add('is-visible');
    feedbackTimer = setTimeout(() => {
      status.classList.remove('is-visible');
      status.textContent = '';
    }, duration);
  }

  // Le partage a réussi : on le compte (voir core/shareRewards.js) et on annonce ce qu'il rapporte, à la suite du message de copie éventuel
  function rewarded(keys) {
    const { fastGained, longLifeGained } = current ? onShared(current) : {};
    if (fastGained) keys.push('share.card.rewardFast');
    if (longLifeGained) keys.push('share.card.rewardLongLife');
    if (keys.length) announce(keys, fastGained ? feedbackMs * 1.6 : feedbackMs);
  }

  // Copie dans le presse-papiers (ordinateur, ou repli). Tout est synchrone jusqu'à l'appel de copie : elle doit partir dans le geste de
  // l'utilisateur (Safari l'exige)
  function copyCard() {
    const content = shareContent({ baseline: t('app.subtitle'), url: target, imageUrl: imageUrl(canvas) });
    return copy(content).then((result) => {
      if (result === 'failed') announce('share.card.copyFailed');
      else rewarded(['share.card.copied']);
      return result;
    });
  }

  // Téléphone : la feuille de partage (image, baseline et lien). Fermée sans choisir : rien d'autre. En échec : retombe sur la copie.
  function shareCard() {
    if (!current) return Promise.resolve(null);
    if (!useNativeShare()) return copyCard();
    return nativeShare({ title: t('share.card.play'), text: t('app.subtitle'), url: target, file: cardFile }).then((result) => {
      if (result === 'shared') rewarded([]);
      return result === 'shared' || result === 'cancelled' ? result : copyCard();
    });
  }

  const dialog = createDialog($('shareOverlay'), {
    trigger: btnOpen,
    // Les cartes proposées : les kamis de la galerie et Ujin, offert à tous (voir core/shareCard.js)
    onOpen: () => { showCard(deck.reset(shareableKamis(getGallery()), current?.index ?? null)); },
    backdropGraceMs: DOUBLE_TAP_MS * 2,
  });
  // « Autre carte » : la suivante du tour ; le premier tour terminé, un message l'annonce (une fois) et la carte reste affichée
  btnNew.addEventListener('click', () => {
    const { index, complete } = deck.next();
    if (complete) announce('share.card.tourDone', feedbackMs * 2);
    else showCard(index);
  });
  btnShare.addEventListener('click', shareCard);

  return {
    open: () => dialog.open(),
    close: dialog.close,
    isOpen: dialog.isOpen,
    btnOpen,
    share: shareCard,
    get card() { return current; },
  };
}
